import { Coordinate, VideoOverlayObject } from '../VideoPlayer/VideoTypes';
import React from 'react';

type Props = {
  currentCoord: Coordinate | undefined;
  trailCoords: Coordinate[];
  proximityLength: number;
  primaryColor: string;
  secondaryColor: string;
  maxX?: number;
  maxY?: number;
};

export const withinBoundaries = (c: Coordinate, maxX: number | undefined, maxY: number | undefined) => {
  return c.x >= 0 && c.y >= 0 && (!maxX || c.x < maxX) && (!maxY || c.y < maxY);
};

const Trail = (props: Props) => {
  const { currentCoord, trailCoords, proximityLength, primaryColor, secondaryColor, maxX, maxY } = props;

  // Calculate trailpath
  let trailPaths: string[] = [''];
  let centerCoord: Coordinate | undefined = undefined;

  trailCoords.forEach((coord: Coordinate) => {
    if (!withinBoundaries(coord, maxX, maxY)) {
      // we skip if it's not within our boundaries, and start a new trail (if we didnt already)
      if (trailPaths[trailPaths.length - 1].length > 0) {
        trailPaths.push('');
      }
    } else {
      // set our center coord if we havent yet (within boundaries)
      if (!centerCoord) {
        centerCoord = coord;
      }

      const index: number = trailPaths.length - 1;
      let currentPath: string = trailPaths[index];
      const isFirst: boolean = currentPath.length === 0;
      currentPath += (isFirst ? 'M ' : 'L ') + coord.x.toString() + ' ' + coord.y.toString() + ' ';
      trailPaths[index] = currentPath;
    }
  });

  if (!centerCoord) {
    return undefined;
  }

  // Calculate proximity path (if we have a current coordinate)
  let proximityPathBefore: string = '';
  let isFirstBefore: boolean = true;
  let proximityPathAfter: string = '';
  let isFirstAfter: boolean = true;
  if (currentCoord) {
    const currentCoordIndex: number = trailCoords.findIndex(
      (c: Coordinate) => c.x === currentCoord.x && c.y === currentCoord.y
    );
    if (currentCoordIndex !== -1 && proximityLength) {
      trailCoords.forEach((trailCoord: Coordinate, index: number) => {
        if (Math.abs(currentCoordIndex - index) < proximityLength) {
          if (index <= currentCoordIndex) {
            proximityPathBefore +=
              (isFirstBefore ? 'M ' : 'L ') + trailCoord.x.toString() + ' ' + trailCoord.y.toString();
            isFirstBefore = false;
          }
          if (index >= currentCoordIndex) {
            proximityPathAfter +=
              (isFirstAfter ? 'M ' : 'L ') + trailCoord.x.toString() + ' ' + trailCoord.y.toString();
            isFirstAfter = false;
          }
        }
      });
    }
  }

  // Create svg
  let i: number = 0;
  const trailSvg = (
    <svg>
      {trailPaths.map((path: string) => {
        return (
          <React.Fragment key={i++}>
            <path fill="none" stroke={primaryColor} strokeWidth={1} d={path} />
            <path fill="none" stroke={secondaryColor} strokeDasharray="10" strokeWidth={1} d={path} />
          </React.Fragment>
        );
      })}

      <path fill="none" stroke="#0af8" strokeWidth={4} d={proximityPathBefore} />
      <path fill="none" stroke="#0f08" strokeWidth={4} d={proximityPathAfter} />
    </svg>
  );

  // Create object
  const trailObject: VideoOverlayObject = {
    svg: trailSvg,
    width: 0,
    height: 0,
    center: centerCoord,
    disableAutoScale: true,
  };

  return trailObject;
};

export default Trail;
